import { type ApolloError, useSubscription } from '@apollo/client'
import { DateTime } from 'luxon'
import { useState } from 'react'

import logger from '@/utils/logger'

import { useIsPageVisible } from '@/client/hooks'
import { SESSION_JOIN_INFO_SUBSCRIPTION } from '@/client/services/hasura'
import type {
  SessionJoinInfoSubscription,
  SessionJoinInfoSubscriptionVariables,
} from '@/client/services/hasura/generated/graphql'

import type { SessionJoinInfo } from '../../types'

export default function useRealtimeSessionJoinInfo(sessionId: number): {
  loading: boolean
  error: ApolloError | null
  session: SessionJoinInfo | null
} {
  const isPageVisible = useIsPageVisible()

  const [session, setSession] = useState<SessionJoinInfo | null>(null)

  const { loading, error = null } = useSubscription<
    SessionJoinInfoSubscription,
    SessionJoinInfoSubscriptionVariables
  >(SESSION_JOIN_INFO_SUBSCRIPTION, {
    variables: {
      id: sessionId,
    },
    onData: data => {
      const sessionFragment = data.data?.data?.session_by_pk
      const sessionDetails =
        sessionFragment == null
          ? null
          : {
              attemptedHosting: sessionFragment.attempted_hosting ?? false,
              startDate: DateTime.fromISO(sessionFragment.start_date),
              endDate: DateTime.fromISO(sessionFragment.end_date),
              zoomEndDate:
                sessionFragment.zoom_end_date == null
                  ? null
                  : DateTime.fromISO(sessionFragment.zoom_end_date),
            }
      setSession(sessionDetails)
    },
    onError: err => {
      logger.warning('Error querying Hasura for session join info', { err })
    },
    skip: !isPageVisible,
  })

  return {
    loading,
    error,
    session,
  }
}
